import { withDependencies, named } from '@wix/thunderbolt-ioc'
import {
	PageFeatureConfigSymbol,
	IPageWillMountHandler,
	CompEventsRegistrarSym,
	ICompEventsRegistrar,
	IPropsStore,
	Props,
} from '@wix/thunderbolt-symbols'
import { name as featureName, ReactionCreatorFactorySymbol } from './symbols'
import { getTriggersCompsToRegister, hasAncestorWithId } from './utils'
import { TriggerTypeEventNameMapper } from './constants'
import type {
	IReactionCreatorFactory,
	TargetCompIdReactions,
	TriggersAndReactionsPageConfig,
	TriggerType,
} from './types'

type RegisterTriggerFunctionParams = {
	compToRegister: string
	triggerType: string
	triggerData: TargetCompIdReactions
	pageId: string
}

const triggersAndReactionsFactory = (
	{ compsToTriggers, repeaterDescendantToRepeaterMapper, isTouchDevice }: TriggersAndReactionsPageConfig,
	reactionCreatorFactory: IReactionCreatorFactory,
	compEventsRegistrar: ICompEventsRegistrar,
	propsStore: IPropsStore
): IPageWillMountHandler => {
	const registerCommonTrigger = ({ compToRegister, triggerData, triggerType }: RegisterTriggerFunctionParams) => {
		compEventsRegistrar.register(
			compToRegister,
			TriggerTypeEventNameMapper[triggerType],
			reactionCreatorFactory.createReaction(triggerData, compToRegister)
		)
	}

	const registerHoverTrigger = ({ compToRegister, triggerData, pageId }: RegisterTriggerFunctionParams) => {
		if (isTouchDevice) {
			let outerClickRegistration: Function | null
			const registerOuterClickCallback = () => {
				if (outerClickRegistration) {
					return
				}

				const reverseReaction = (event: Event) => {
					event.stopPropagation()
					if (hasAncestorWithId(event.target, compToRegister)) {
						return
					}
					reactionCreatorFactory.createReverseReaction(triggerData, compToRegister)()
					outerClickRegistration && compEventsRegistrar.unregister(pageId, 'onClick', outerClickRegistration)
					outerClickRegistration = null
				}

				outerClickRegistration = compEventsRegistrar.register(pageId, 'onClick', reverseReaction)
			}
			compEventsRegistrar.register(
				compToRegister,
				'onClick',
				reactionCreatorFactory.createReaction(triggerData, compToRegister, registerOuterClickCallback)
			)
		} else {
			compEventsRegistrar.register(
				compToRegister,
				'onMouseEnter',
				reactionCreatorFactory.createReaction(triggerData, compToRegister)
			)
			compEventsRegistrar.register(
				compToRegister,
				'onMouseLeave',
				reactionCreatorFactory.createReverseReaction(triggerData, compToRegister)
			)
		}
	}

	const triggerTypeHandler: { [index: string]: Function } = {
		click: registerCommonTrigger,
		tap: registerCommonTrigger,
		'mouse-in': registerCommonTrigger,
		'mouse-out': registerCommonTrigger,
		hover: registerHoverTrigger,
	}

	return {
		async pageWillMount(pageId: string) {
			// register triggers
			Object.keys(compsToTriggers).forEach((compId) => {
				const compTriggerData = compsToTriggers[compId]
				const compsToRegister = getTriggersCompsToRegister(
					compId,
					propsStore,
					repeaterDescendantToRepeaterMapper
				)
				for (const compToRegister of compsToRegister) {
					Object.keys(compTriggerData).forEach((triggerType) => {
						const triggerData = compTriggerData[triggerType as TriggerType] as TargetCompIdReactions
						const registerTriggerFunction = triggerTypeHandler[triggerType]
						registerTriggerFunction({ compToRegister, triggerData, triggerType, pageId })
					})
				}
			})
		},
	}
}

export const TriggersAndReactions = withDependencies(
	[named(PageFeatureConfigSymbol, featureName), ReactionCreatorFactorySymbol, CompEventsRegistrarSym, Props],
	triggersAndReactionsFactory
)
